import { buildJsonTree, filterJsonTree } from "./json-tree-core.js";
import { countText, decodeUtf8File, validateTextFile } from "./data-tool-core.js";
import "./data-tool.css";

const root = document.querySelector("[data-json-tree]");
if (root) {
  const ids = [
    "jsonTreeStatus", "jsonTreeSearch", "jsonTreeFile", "chooseJsonTreeFile", "loadJsonTreeExample",
    "expandJsonTree", "collapseJsonTree", "clearJsonTree", "jsonTreeSource", "jsonTreeFileName",
    "jsonTreeSourceCount", "jsonTreeResultTitle", "jsonTreeSummary", "jsonTreeResult", "buildJsonTree"
  ];
  const elements = Object.fromEntries(ids.map(id => [id, document.getElementById(id)]));
  const dropCard = root.querySelector("[data-json-tree-drop]");
  let tree = null;

  function showStatus(message = "", kind = "error") {
    elements.jsonTreeStatus.textContent = message;
    elements.jsonTreeStatus.dataset.kind = kind;
    elements.jsonTreeStatus.hidden = !message;
    if (message && kind === "error") window.toolboxAnalytics?.track("tool_error", { error_category: "validation" });
  }

  function updateCount() {
    const count = countText(elements.jsonTreeSource.value);
    elements.jsonTreeSourceCount.textContent = `${count.characters.toLocaleString("en-US")} characters · ${count.lines.toLocaleString("en-US")} lines`;
  }

  function changed() {
    tree = null;
    elements.jsonTreeSearch.value = "";
    elements.jsonTreeSearch.disabled = true;
    elements.expandJsonTree.disabled = true;
    elements.collapseJsonTree.disabled = true;
    elements.jsonTreeSummary.textContent = "Build the tree to inspect nested values.";
    elements.jsonTreeResult.innerHTML = '<p class="json-tree-empty">Your expandable JSON tree will appear here.</p>';
    showStatus();
    updateCount();
  }

  function render() {
    const filtered = filterJsonTree(tree, elements.jsonTreeSearch.value);
    elements.jsonTreeResult.replaceChildren();
    if (!filtered) {
      const empty = document.createElement("p");
      empty.className = "json-tree-empty";
      empty.textContent = "No nodes match this search.";
      elements.jsonTreeResult.append(empty);
      return;
    }
    elements.jsonTreeResult.append(renderNode(filtered, 0, Boolean(elements.jsonTreeSearch.value)));
  }

  function renderNode(node, depth, searching) {
    const hasChildren = node.children.length > 0 || node.type === "array" || node.type === "object";
    const row = document.createElement("div");
    row.className = "json-tree-node";
    if (!hasChildren) {
      row.classList.add("json-tree-leaf");
      row.append(nodeLabel(node), pathButton(node.path));
      return row;
    }
    const details = document.createElement("details");
    details.open = searching || depth < 2;
    const summary = document.createElement("summary");
    summary.append(nodeLabel(node), countBadge(node));
    const copy = pathButton(node.path);
    copy.addEventListener("click", event => event.stopPropagation());
    summary.append(copy);
    details.append(summary);
    const children = document.createElement("div");
    children.className = "json-tree-children";
    for (const child of node.children) children.append(renderNode(child, depth + 1, searching));
    details.append(children);
    row.append(details);
    return row;
  }

  function nodeLabel(node) {
    const wrapper = document.createElement("span");
    wrapper.className = "json-tree-label";
    const key = document.createElement("span");
    key.className = "json-tree-key";
    key.textContent = node.key === null ? "$" : String(node.key);
    const type = document.createElement("span");
    type.className = `json-tree-type type-${node.type}`;
    type.textContent = node.type;
    wrapper.append(key, type);
    if (node.value !== null) {
      const value = document.createElement("span");
      value.className = `json-tree-value type-${node.type}`;
      value.textContent = node.type === "string" ? JSON.stringify(node.value) : node.value;
      wrapper.append(value);
    }
    return wrapper;
  }

  function countBadge(node) {
    const badge = document.createElement("span");
    badge.className = "json-tree-count";
    badge.textContent = `${node.count} ${node.type === "array" ? "items" : "keys"}`;
    return badge;
  }

  function pathButton(path) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "json-tree-path";
    button.textContent = path;
    button.title = `Copy ${path}`;
    button.addEventListener("click", async event => {
      event.preventDefault();
      event.stopPropagation();
      try {
        await navigator.clipboard.writeText(path);
        showStatus(`Copied path ${path}.`, "success");
      } catch {
        showStatus("Copying is unavailable.");
      }
    });
    return button;
  }

  function build() {
    const result = buildJsonTree(elements.jsonTreeSource.value);
    if (result.error) {
      changed();
      showStatus(result.error);
      return;
    }
    tree = result.tree;
    elements.jsonTreeSearch.disabled = false;
    elements.expandJsonTree.disabled = false;
    elements.collapseJsonTree.disabled = false;
    elements.jsonTreeSummary.textContent = `${result.nodes.toLocaleString("en-US")} nodes · ${result.maxDepth.toLocaleString("en-US")} levels deep`;
    render();
    showStatus("Valid JSON tree built.", "success");
    window.toolboxAnalytics?.track("tool_complete");
    elements.jsonTreeResultTitle.focus();
  }

  async function loadFile(file) {
    const error = validateTextFile(file, ["json"], "JSON");
    if (error) return showStatus(error);
    try {
      elements.jsonTreeSource.value = decodeUtf8File(new Uint8Array(await file.arrayBuffer()));
      elements.jsonTreeFileName.textContent = file.name;
      changed();
      elements.jsonTreeSource.focus();
    } catch (error) {
      showStatus(error?.message || "This JSON file could not be read.");
    } finally {
      elements.jsonTreeFile.value = "";
    }
  }

  elements.chooseJsonTreeFile.addEventListener("click", () => elements.jsonTreeFile.click());
  elements.jsonTreeFile.addEventListener("change", () => loadFile(elements.jsonTreeFile.files?.[0]));
  elements.jsonTreeSource.addEventListener("input", changed);
  elements.buildJsonTree.addEventListener("click", build);
  elements.loadJsonTreeExample.addEventListener("click", () => {
    window.toolboxAnalytics?.track("tool_start");
    elements.jsonTreeSource.value = JSON.stringify({
      project: "smallpdf.app",
      tools: [{ name: "JSON Formatter", active: true }, { name: "JSON Tree Viewer", active: true }],
      settings: { theme: "light", limits: { files: 1 } }
    }, null, 2);
    elements.jsonTreeFileName.textContent = "example.json";
    changed();
    build();
  });
  elements.clearJsonTree.addEventListener("click", () => {
    elements.jsonTreeSource.value = "";
    elements.jsonTreeFileName.textContent = "JSON document";
    changed();
    elements.jsonTreeSource.focus();
  });
  elements.jsonTreeSearch.addEventListener("input", render);
  elements.expandJsonTree.addEventListener("click", () => elements.jsonTreeResult.querySelectorAll("details").forEach(item => { item.open = true; }));
  elements.collapseJsonTree.addEventListener("click", () => elements.jsonTreeResult.querySelectorAll("details").forEach(item => { item.open = false; }));
  dropCard.addEventListener("dragover", event => {
    event.preventDefault();
    dropCard.classList.add("is-dragging");
  });
  dropCard.addEventListener("dragleave", () => dropCard.classList.remove("is-dragging"));
  dropCard.addEventListener("drop", event => {
    event.preventDefault();
    dropCard.classList.remove("is-dragging");
    loadFile(event.dataTransfer.files?.[0]);
  });
  changed();
}
